import { ReactNode, useEffect, useRef, useState } from 'react';

export interface StepValidationFailure {
  message: string;
  /** DOM id of the field the user must fix; the stepper will scroll/focus it. */
  focusId?: string;
}

export interface StepDef {
  id: string;
  title: string;
  /** Render the body when expanded; receive `next` to advance + `setError` to surface validation. */
  body: (ctx: {
    next: () => void;
    setError: (m: string | null) => void;
    error: string | null;
  }) => ReactNode;
  /** One-line summary shown when collapsed (post-completion). Return null to hide. */
  summary: () => ReactNode | null;
  /**
   * Returns null when the step is valid. On failure, returns the message + an
   * optional `focusId` of the offending field so the stepper can scroll to it
   * and surface the inline highlight.
   */
  validate: () => StepValidationFailure | null;
  /**
   * If true, hide the stepper's bottom "Continue" button — the body owns the
   * primary CTA and is responsible for calling `next()` on success.
   */
  hideContinue?: boolean;
}

function focusField(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  // jsdom doesn't implement scrollIntoView — guard so unit tests don't crash.
  if (typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  // Focus runs after the scroll starts; for non-focusable wrappers, fall back
  // to no-op (scroll alone is the visual cue + the field-invalid class draws
  // the red box).
  if (typeof (el as HTMLElement).focus === 'function') {
    (el as HTMLElement).focus({ preventScroll: true });
  }
}

function scrollStepIntoView(id: string) {
  const el = document.querySelector(`[data-testid="step-${id}"]`);
  if (typeof el?.scrollIntoView === 'function') {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

export function CheckoutStepper({
  steps,
  onComplete,
  onStepView,
}: {
  steps: StepDef[];
  onComplete: () => void;
  onStepView?: (step: StepDef, index: number) => void;
}) {
  const [activeIdx, setActiveIdx] = useState(0);
  const viewedStepIds = useRef<Set<string>>(new Set());
  const [completed, setCompleted] = useState<Set<string>>(new Set());
  // Per-step error state. `inline=true` means the failing step also rendered a
  // field-level error in its body — the stepper suppresses its own bottom
  // alert in that case so the user doesn't see the same message twice.
  const [errors, setErrors] = useState<Record<string, { message: string; inline: boolean } | null>>(
    {}
  );
  // Per-step "flash error" animation flag on the Continue button. Toggled
  // briefly on validate-failure so the button pulses red→gray instead of
  // permanently outlining the offending field with a red ring.
  const [flashing, setFlashing] = useState<Record<string, number>>({});

  const setStepError = (id: string, m: { message: string; inline: boolean } | null) =>
    setErrors((cur) => ({ ...cur, [id]: m }));

  const advance = (id: string) => {
    setCompleted((cur) => new Set(cur).add(id));
    setStepError(id, null);
    if (activeIdx >= steps.length - 1) {
      onComplete();
    } else {
      const nextIdx = activeIdx + 1;
      setActiveIdx(nextIdx);
      requestAnimationFrame(() => scrollStepIntoView(steps[nextIdx].id));
    }
  };

  const tryAdvance = (idx: number) => {
    const step = steps[idx];
    const result = step.validate();
    if (result) {
      setStepError(step.id, { message: result.message, inline: Boolean(result.focusId) });
      // Bump the flash counter so the same error firing twice in a row
      // re-triggers the keyframe animation (React would otherwise diff a
      // boolean-false→true→false flip without re-rendering between).
      setFlashing((cur) => ({ ...cur, [step.id]: (cur[step.id] ?? 0) + 1 }));
      window.setTimeout(() => setFlashing((cur) => ({ ...cur, [step.id]: 0 })), 1300);
      if (result.focusId) {
        // Defer until after React paints so the user sees the inline error
        // text + scroll happen in concert with the button flash.
        requestAnimationFrame(() => focusField(result.focusId!));
      }
      return;
    }
    advance(step.id);
  };

  const goTo = (idx: number) => setActiveIdx(idx);

  useEffect(() => {
    const step = steps[activeIdx];
    if (!step || viewedStepIds.current.has(step.id)) return;
    viewedStepIds.current.add(step.id);
    onStepView?.(step, activeIdx);
  }, [activeIdx, onStepView, steps]);

  return (
    <ol className="grid gap-3" aria-label="Checkout steps">
      {steps.map((step, idx) => {
        const isActive = idx === activeIdx;
        const isCompleted = completed.has(step.id);
        const stepError = errors[step.id] ?? null;
        return (
          <li
            key={step.id}
            className={`scroll-mt-24 overflow-hidden rounded-lg border ${
              isActive ? 'border-accent/60' : 'border-bone/15 bg-neutral-900/40'
            }`}
            data-testid={`step-${step.id}`}
          >
            <button
              type="button"
              onClick={() => goTo(idx)}
              disabled={!isCompleted && !isActive}
              className="flex w-full items-center justify-between gap-3 p-4 text-left disabled:cursor-not-allowed"
              aria-expanded={isActive}
            >
              <div className="flex items-center gap-3">
                <span
                  className={`grid h-7 w-7 place-items-center rounded-full border text-xs ${
                    isCompleted
                      ? 'border-accent bg-accent text-ink'
                      : isActive
                        ? 'border-accent text-accent'
                        : 'border-bone/30 text-bone/40'
                  }`}
                >
                  {isCompleted ? '✓' : idx + 1}
                </span>
                <div>
                  <div className="font-display text-lg">{step.title}</div>
                  {!isActive && isCompleted && (
                    <div className="text-xs text-bone/60">{step.summary()}</div>
                  )}
                </div>
              </div>
              {isCompleted && !isActive && (
                <span className="text-xs uppercase tracking-wider text-accent">Edit</span>
              )}
            </button>
            {isActive && (
              <div className="border-t border-bone/10 p-4">
                {step.body({
                  next: () => tryAdvance(idx),
                  setError: (m) => setStepError(step.id, m ? { message: m, inline: false } : null),
                  error: stepError?.message ?? null,
                })}
                {stepError && !stepError.inline && (
                  <div
                    role="alert"
                    className="mt-3 rounded border border-accent/50 bg-accent/10 p-3 text-sm text-accent"
                  >
                    {stepError.message}
                  </div>
                )}
                {!step.hideContinue && (
                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      onClick={() => tryAdvance(idx)}
                      // The flash counter resets to 0 after 1.3s; while non-zero
                      // the button is mid-pulse. The key forces a remount so a
                      // second consecutive failure re-runs the animation.
                      key={`continue-${step.id}-${flashing[step.id] ?? 0}`}
                      className={`btn-primary ${flashing[step.id] ? 'flash-error-button' : ''}`}
                      data-testid={`step-continue-${step.id}`}
                    >
                      {idx === steps.length - 1 ? 'Continue to checkout' : 'Continue'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
